import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMapPin, FiClock, FiCar, FiPhone, FiNavigation } = FiIcons;

function Location() {
  const directions = [
    {
      from: 'Colombo via A4',
      time: '3.5 hours',
      distance: '125 km',
      route: 'Colombo → Ratnapura → Pelmadulla → Kolonna'
    },
    {
      from: 'Mattala Airport',
      time: '1.5 hours',
      distance: '48 km',
      route: 'HRI → Embilipitiya → Kolonna'
    },
    {
      from: 'Galle via Deniyaya',
      time: '2.5 hours',
      distance: '85 km',
      route: 'Galle → Akuressa → Deniyaya → Kolonna'
    }
  ];

  const nearbyAttractions = [
    {
      name: 'Sinharaja Morning Side',
      distance: '15 km',
      time: '45 min (4x4)',
      description: 'UNESCO World Heritage rainforest with endemic species'
    },
    {
      name: 'Suriyakanda Viewpoint',
      distance: '12 km',
      time: '30 min',
      description: 'Panoramic views over the rainforest canopy'
    },
    {
      name: 'Wawulpane Limestone Cave',
      distance: '8 km',
      time: '20 min',
      description: 'Ancient limestone formations and underground chambers'
    }
  ];

  return (
    <section id="location" className="py-20 bg-gradient-to-b from-slate-800 to-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-white mb-6">
            Find Your Way to
            <span className="gradient-text block">Adventure</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Located in the heart of Ullinduwawa, Kolonna, we're easily accessible 
            from major cities while being perfectly positioned for rainforest exploration.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Map Section */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            {/* Interactive Map Placeholder */}
            <div className="glass-morphism rounded-2xl p-6 h-80 flex items-center justify-center bg-gradient-to-br from-emerald-900/20 to-slate-900/20">
              <div className="text-center space-y-4">
                <SafeIcon icon={FiMapPin} className="w-16 h-16 text-emerald-400 mx-auto" />
                <h3 className="text-xl font-semibold text-white">Interactive Map</h3>
                <p className="text-gray-300">Ullinduwawa, Kolonna, Ratnapura District</p>
                <a
                  href="https://maps.google.com/?q=Ullinduwawa,+Kolonna"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl transition-colors duration-300"
                >
                  <SafeIcon icon={FiNavigation} className="w-5 h-5" />
                  <span>Get Directions</span>
                </a>
              </div>
            </div>

            {/* Contact Info */}
            <div className="glass-morphism rounded-2xl p-6">
              <h3 className="text-xl font-semibold text-white mb-4">Contact Information</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3 text-gray-300">
                  <SafeIcon icon={FiMapPin} className="w-5 h-5 text-emerald-400" />
                  <span>Ullinduwawa, Kolonna, Ratnapura District</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-300">
                  <SafeIcon icon={FiPhone} className="w-5 h-5 text-emerald-400" />
                  <span>+94 77 123 4567</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-300">
                  <SafeIcon icon={FiCar} className="w-5 h-5 text-emerald-400" />
                  <span>Free Secure Parking Available</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Directions & Attractions */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            {/* Directions */}
            <div className="glass-morphism rounded-2xl p-6">
              <h3 className="text-xl font-semibold text-white mb-6">How to Reach Us</h3>
              <div className="space-y-4">
                {directions.map((direction, index) => (
                  <motion.div
                    key={direction.from}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.6 }}
                    className="border border-emerald-500/20 rounded-xl p-4 hover:border-emerald-500/40 transition-colors duration-300"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-semibold text-white">{direction.from}</h4>
                      <div className="text-right text-sm">
                        <div className="text-emerald-400 font-semibold">{direction.time}</div>
                        <div className="text-gray-400">{direction.distance}</div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-300">{direction.route}</p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Nearby Attractions */}
            <div className="glass-morphism rounded-2xl p-6">
              <h3 className="text-xl font-semibold text-white mb-6">Nearby Attractions</h3>
              <div className="space-y-4">
                {nearbyAttractions.map((attraction, index) => (
                  <motion.div
                    key={attraction.name}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.6 }}
                    className="border border-emerald-500/20 rounded-xl p-4 hover:border-emerald-500/40 transition-colors duration-300"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-semibold text-white">{attraction.name}</h4>
                      <div className="text-right text-sm">
                        <div className="text-emerald-400 font-semibold">{attraction.distance}</div>
                        <div className="text-gray-400">{attraction.time}</div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-300">{attraction.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="mt-16 glass-morphism rounded-2xl p-8 text-center"
        >
          <h3 className="text-2xl font-serif font-bold text-white mb-4">
            Need Transportation Assistance?
          </h3>
          <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
            We can arrange pickup services from airports or help coordinate 4x4 transfers 
            to the Morning Side entrance. Contact us for personalized assistance.
          </p>
          <a
            href="https://wa.me/94771234567"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-8 rounded-xl transition-all duration-300"
          >
            <SafeIcon icon={FiPhone} className="w-5 h-5" />
            <span>Contact for Transport</span>
          </a>
        </motion.div>
      </div>
    </section>
  );
}

export default Location;